import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { fetchVouchers } from "@/lib/api";
import type { Voucher } from "@/types/wallet";

/**
 * `state.vouchers` — the tenant's voucher catalogue.
 *
 * In the original this arrives over the POS socket (`socketFetchedVouchers`)
 * and is never fetched; we have no socket and must not add one, so the same
 * catalogue is loaded over REST at the storefront's existing poll points. The
 * SHAPE is the socket's: whole documents, filtered client-side.
 *
 * The wallet reads it for two things — the voucher rows behind the Vouchers
 * tile, and `findLinkedVoucherOffers`, which maps a typed code to the offers a
 * voucher unlocks.
 */
interface VouchersState {
  data: Voucher[];
  loading: boolean;
}

const initialState: VouchersState = { data: [], loading: false };

export const loadVouchers = createAsyncThunk(
  "vouchers/load",
  async (tenantId: string) => fetchVouchers(tenantId),
);

const vouchersSlice = createSlice({
  name: "vouchers",
  initialState,
  reducers: {
    setVouchers(state, action: PayloadAction<Voucher[] | null>) {
      state.data = action.payload ?? [];
    },
    resetVouchers: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadVouchers.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadVouchers.fulfilled, (state, action) => {
        state.data = action.payload ?? [];
        state.loading = false;
      })
      .addCase(loadVouchers.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { setVouchers, resetVouchers } = vouchersSlice.actions;
export default vouchersSlice.reducer;
